<template>
    <div ref="section" class="padded-container">
        <div class="aa-funnel-grid aa-funnel-grid--reseller">
            <div>
                <LoadingSpinner></LoadingSpinner>
            </div>
            <div>
                <h3></h3>
                <div></div>
            </div>
        </div>
    </div>
</template>

<script>
    import BranchIOSetup from 'aa/vue/components/branchIO/BranchIOSetup';
    import LoadingSpinner from 'atoms/LoadingSpinner';
    import { mapGetters } from 'vuex';
    import debounce from 'lodash/debounce';
    import Breakpoints from 'helpers/breakpoints';
    import { IOS_SIGNUP } from 'aa/vue/constants/shared';
    import { SHOW_PICKER_PATH } from 'aa/vue/constants/aaRoutes';
    import { MFE_ENABLED, MFE_USERPROFILES_ENABLED } from 'aa/helpers/featureConstants';
    import { tracking } from 'services/Tracking';
    import { PAGE_TYPES, ACTION_NAMES } from 'aa/vue/constants/aaTrackingConstants';

    const breakpointsObject = new Breakpoints();

    export default {
        name: 'ResellerJourney',
        data() {
            // These are minimum values
            // Minimums are needed because the screen is smaller than the combined height of the footer... header... and iframe.
            // So a calculation based on the screen height minus the header and footer heights needs a minimum to work.
            return {
                headerHeight: '56px',
                iframeMinHeight: {
                    mobile: '512px',
                    tablet: '720px',
                    desktopWide: '882px',
                },
                iframeSectionMinHeight: {
                    mobile: '564px',
                    tablet: '800px',
                    desktopWide: '944px',
                },
                iframeId: '#branch-banner-iframe',
                debounceWindowResize: debounce(this.handleScreenSizeChange, 500),
                breakpoints: breakpointsObject.getBreakpoints(),
            };
        },
        props: {
            type: {
                type: String,
                required: true,
                default: () => {
                    return '';
                },
            },
        },
        unmounted() {
            window.removeEventListener('resize', this.debounceWindowResize);
        },
        mounted() {
            // change css as screen height changes
            window.addEventListener('resize', this.debounceWindowResize);

            // added here extra check to not block users;
            if (this.getBranchJourneyKey) {
                this.invokeBranchJourney();
            } else {
                this.handleNext();
            }
        },
        methods: {
            invokeBranchJourney() {
                const branchIo = new BranchIOSetup(this.getConfig());
                if (this.isAbTestFlow()) {
                    try {
                        if (!window.branch) {
                            this.handleNext();
                        }
                    } catch (e) {}
                }

                // add listeners from BranchIOSetup
                branchIo.dispatcher.addEventListener('didShowJourney', () => {
                    this.handleDidShowJourney();
                });
                branchIo.dispatcher.addEventListener('didClickJourneyContinue', () => {
                    this.handleDidClickJourney();
                });
                branchIo.dispatcher.addEventListener('didClickJourneyCTA', () => {
                    try {
                        if (this.isAbTestFlow()) {
                            this.handleAbTestTracking();
                        }
                    } catch (e) {}
                    /*
                     * branch io trigger didClickJourneyCTA event when User click on Journey's CTA
                     * branch io fires event didClickJourneyCTA early and take some time to redirect user to app OR app store
                     * so before branch io redirection we can not make any internal redirect for mweb user
                     * that's why we added a 12 sec delay here to not stuck user on loading screen
                     * after redirection when user will open mweb again user will see home(logged in) OR show picker page(newly created user)
                     */
                    setTimeout(() => {
                        this.handleDidClickJourney();
                    }, 12000);
                });
                branchIo.dispatcher.addEventListener('willNotShowJourney', () => {
                    this.handleWillNotShowJourney();
                });
            },

            getConfig() {
                return {
                    key: this.getBranchJourneyKey,
                    autoLoginEnabled: this.getAutoLoginInfo(),
                    flowType: this.getFlowType(),
                    isAbTestFlow: this.isAbTestFlow(),
                };
            },
            getAutoLoginInfo() {
                if (this.isAbTestFlow()) {
                    return true;
                }
                return this.deltaAutoLoginEnabled;
            },
            handleAbTestTracking() {
                tracking.trackAction(ACTION_NAMES.TRACK_APP_REDIRECT, {
                    linkName: ACTION_NAMES.TRACK_APP_REDIRECT,
                    pageURL: window.location.href,
                    pageType: PAGE_TYPES.SIGNUP_COMPLETE,
                    userStatus: this.$store.getters['user/trackingUserStatus'],
                    userType: this.$store.state.user.svod.status,
                });
            },

            getIframeMinHeight() {
                if (window.innerWidth > this.breakpoints.desktopWide.min) {
                    return this.iframeMinHeight.desktopWide;
                }
                return window.innerWidth > this.breakpoints.mobilePortrait.max
                    ? this.iframeMinHeight.tablet
                    : this.iframeMinHeight.mobile;
            },

            getIframeSectionMinHeight() {
                if (window.innerWidth > this.breakpoints.desktopWide.min) {
                    return this.iframeSectionMinHeight.desktopWide;
                }
                return window.innerWidth > this.breakpoints.mobilePortrait.max
                    ? this.iframeSectionMinHeight.tablet
                    : this.iframeSectionMinHeight.mobile;
            },

            heightCss(minHeight) {
                const footerHeight = document.querySelector('footer').clientHeight;
                return `max(${minHeight}, calc(100vh - ${this.headerHeight} - ${footerHeight}px))`;
            },

            handleScreenSizeChange() {
                const iframe = document.querySelector(this.iframeId);

                //update the iframe height
                iframe.style.height = this.heightCss(this.getIframeMinHeight());

                //update the iframe containing section height
                this.$refs.section.style.height = this.heightCss(this.getIframeSectionMinHeight());
            },

            handleDidShowJourney() {
                const iframe = document.querySelector(this.iframeId);
                const mainAAContainer = document.querySelector('#main-aa-container');
                const globalHeaderContainer = document.querySelector('#global-header-container');
                const bodyElement = document.querySelector('body');
                const headerNavSubMenu = document.querySelector('.header__nav--withsubMenu');

                //remove space above and below the body element
                bodyElement.style.marginTop = 0;
                bodyElement.style.marginBottom = 0;
                bodyElement.style.minHeight = '100vh';

                //menu dropdown should be hidden, as per Product requirements
                headerNavSubMenu.style.display = 'none';

                // set iframe props
                iframe.style.top = this.headerHeight;
                iframe.style.height = this.heightCss(this.getIframeMinHeight());
                iframe.style.position = 'absolute';

                //remove scroll bar from main-aa-container
                mainAAContainer.style.overflow = 'visible';

                //make the global header non-fixed
                globalHeaderContainer.style.position = 'absolute';

                // set component props
                this.$refs.section.style.top = this.headerHeight;
                this.$refs.section.style.height = this.heightCss(this.getIframeSectionMinHeight());
            },

            /*
             * we will redirect user to Home (if the user had just signed in) or Show Picker (if the user had just created an account).
             * user will get redirect on both cases
             * 1:user clicked on Download/Open Now CTA
             * OR
             * 2:Maybe Later CTA,
             * */
            handleDidClickJourney() {
                this.handleNext();
            },

            handleWillNotShowJourney() {
                this.handleNext();
            },

            handleNext() {
                if (this.type === 'signup') {
                    this.pushToShowPicker();
                } else {
                    this.handleSignIn();
                }
            },
            isAbTestFlow() {
                return this.flowType === IOS_SIGNUP;
            },

            removeHyphens(str) {
                return str.replace(/-/g, '');
            },

            getFlowType() {
                if (this.isAbTestFlow()) {
                    return this.removeHyphens(IOS_SIGNUP);
                }

                return this.flowType;
            },

            pushToShowPicker() {
                if (this.flowType === IOS_SIGNUP) {
                    this.handleIOSBilling();
                    return;
                }
                this.$router.push({
                    name: 'ResellerShowPicker',
                });
            },
            handleIOSBilling() {
                const showMFEUserProfile =
                    this.$store.state.featureFlags[MFE_ENABLED] &&
                    this.$store.state.featureFlags[MFE_USERPROFILES_ENABLED];
                if (showMFEUserProfile) {
                    window.location.href = SHOW_PICKER_PATH;
                    return;
                } else {
                    this.$router.replace({ name: 'ShowsPicker' });
                    return;
                }
            },
            handleSignIn() {
                window.location.href = this.$store.getters['flow/destinationUrl'];
            },
        },
        computed: {
            ...mapGetters({
                getBranchJourneyKey: 'getBranchJourneyKey',
                deltaAutoLoginEnabled: 'featureFlags/deltaAutoLoginEnabled',
                flowType: 'flow/type',
            }),
        },
        components: {
            LoadingSpinner,
        },
    };
</script>
